import React from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import {
  Item,
  SimpleSingleDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import DatePicker from '@grnhse/seedling/lib/birch/components/forms/date_picker';
import { AppliedOnType } from 'talent_matching/types';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { parseDate, formatDate } from 'talent_search/lib/utils';
import { subDays } from 'date-fns';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.filter_row.application_date');

const DATE_TYPE_OPTIONS = Object.values(AppliedOnType).map((value) => ({
  label: t(`range.${value}`),
  value,
}));

const ApplicationDate = () => {
  const { userDateFormat } = useUserContext();
  const { setValue, watch } = useFormContext();
  const [appliedOnStart, appliedOnEnd, appliedOnType] = watch([
    'appliedOnStart',
    'appliedOnEnd',
    'appliedOnType',
  ]);

  const startDate = parseDate(appliedOnStart, userDateFormat);
  const endDate = parseDate(appliedOnEnd, userDateFormat);

  const today = new Date();
  const yesterday = subDays(today, 1);
  const sevenDaysAgo = subDays(today, 7);

  const selectedType =
    DATE_TYPE_OPTIONS.find((option) => option.value === appliedOnType) || null;

  const showStartDate = [
    AppliedOnType.BETWEEN,
    AppliedOnType.ON_OR_AFTER,
  ].includes(appliedOnType);

  const showEndDate = [
    AppliedOnType.BETWEEN,
    AppliedOnType.ON_OR_BEFORE,
  ].includes(appliedOnType);

  const setDate = (
    type: 'appliedOnStart' | 'appliedOnEnd',
    newDate: Date | null
  ) => {
    setValue(type, formatDate(newDate, userDateFormat), { shouldDirty: true });
  };

  const onTypeChange = (newType: Item | null) => {
    setValue('appliedOnType', newType?.value, { shouldDirty: true });

    switch (newType?.value) {
      case AppliedOnType.TODAY:
        setDate('appliedOnStart', today);
        setDate('appliedOnEnd', today);
        break;
      case AppliedOnType.YESTERDAY:
        setDate('appliedOnStart', yesterday);
        setDate('appliedOnEnd', yesterday);
        break;
      case AppliedOnType.LAST_SEVEN_DAYS:
        setDate('appliedOnStart', sevenDaysAgo);
        setDate('appliedOnEnd', today);
        break;
      default:
        setDate('appliedOnStart', null);
        setDate('appliedOnEnd', null);
    }
  };

  return (
    <Stack spacing={2} direction="column">
      <SimpleSingleDropdown
        ariaLabel={t('range_placeholder')}
        placeholder={t('range_placeholder')}
        items={DATE_TYPE_OPTIONS}
        selectedItem={selectedType}
        onSelectedItemChange={onTypeChange}
      />
      {showStartDate && (
        <DatePicker
          label={t('start')}
          onChange={(value) => setDate('appliedOnStart', value)}
          dateFormat={userDateFormat}
          selected={startDate}
          maxDate={today}
        />
      )}
      {showEndDate && (
        <DatePicker
          label={t('end')}
          onChange={(value) => setDate('appliedOnEnd', value)}
          dateFormat={userDateFormat}
          selected={endDate}
          maxDate={today}
        />
      )}
    </Stack>
  );
};

export default ApplicationDate;
